import pino from "pino/browser.js";
import { DefaultLogLevels } from "../types.js";
import type { PinoModuleString } from "../pinoModules.js";
import type { RootLoggerType } from "../types.js";

type BrowserLogRecord = Record<string, unknown> & { message?: unknown; msg?: unknown };
type BrowserConsoleMethod = (...args: unknown[]) => void;
type BrowserLoggerLevel = Exclude<keyof typeof DefaultLogLevels, "silent">;

function toMessage(record: BrowserLogRecord): string {
    if (typeof record.message === "string") return record.message;
    if (typeof record.msg === "string") return record.msg;
    return "";
}

function writeLogWithLevel(method: (...args: unknown[]) => void, level: string) {
    return (raw: object): void => {
        const { message, msg, ...rest } = raw as BrowserLogRecord;
        const text = toMessage({ message, msg });
        const formatted = `[${level}]${text.length > 0 ? ` ${text}` : ""}`;
        Object.keys(rest).length === 0 ? method(formatted) : method(formatted, rest);
    };
}

const con = globalThis.console;
const browserMethodByLevel: Record<BrowserLoggerLevel, BrowserConsoleMethod> = {
    trace: con.debug.bind(con),
    debug: con.debug.bind(con),
    info: con.info.bind(con),
    warn: con.warn.bind(con),
    error: con.error.bind(con),
    fatal: con.error.bind(con),
};

function buildBrowserWriters(): Record<BrowserLoggerLevel, (record: object) => void> {
    const levels = (Object.keys(browserMethodByLevel) as BrowserLoggerLevel[]).sort(
        (a, b) => DefaultLogLevels[a] - DefaultLogLevels[b],
    );
    return Object.fromEntries(
        levels.map((level) => [level, writeLogWithLevel(browserMethodByLevel[level], level.toUpperCase())]),
    ) as Record<BrowserLoggerLevel, (record: object) => void>;
}

/**
 * Creates the browser logging implementation: pino/browser.js writing formatted
 * lines to the matching console method per level.
 */
export function createBrowserLogger<TModuleString extends string = PinoModuleString>(): RootLoggerType<TModuleString> {
    return pino({
        name: "frontend",
        level: "debug",
        messageKey: "message",
        errorKey: "error",
        formatters: { level: (label) => ({ level: label }) },
        browser: {
            asObject: true,
            write: buildBrowserWriters(),
        },
    }) as RootLoggerType<TModuleString>;
}
